/**
 * Team Page - Dynamic team members fetched from database
 * @file src/web/features/main/pages/team.tsx
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../../shared/components';
import { useTeam, TeamMember } from '../../team/hooks/useTeam';
import { Loader2, User } from 'lucide-react';

// --- Reusable Teacher Card Component (Vertical Layout) ---
const TeacherCard: React.FC<{ member: TeamMember; delay: number }> = ({ member, delay }) => (
  <div className={`bg-white rounded-xl shadow-lg text-foreground pt-6 pb-6 px-6 animate-slide-up animation-delay-${delay}`}>
    <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-6">
      {member.imageUrl ? (
        <img
          src={member.imageUrl}
          alt={member.name}
          className="w-full h-full object-cover object-top"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-cyan-100">
          <User className="w-10 h-10 text-cyan-600" />
        </div>
      )}
    </div>
    <div className="text-center">
      <h3 className="text-xl font-display font-bold mb-1">{member.name}</h3>
      <p className="text-accent font-medium mb-2">{member.position}</p>
      {member.qualification && (
        <p className="text-sm text-muted-foreground mb-4">{member.qualification}</p>
      )}
      {member.bio && (
        <p className="text-muted-foreground text-sm">{member.bio}</p>
      )}
    </div>
  </div>
);

// --- Leadership Card Component (Horizontal Layout) ---
const LeadershipCard: React.FC<{ member: TeamMember }> = ({ member }) => (
  <div className="bg-white rounded-xl shadow-lg text-foreground flex flex-col md:flex-row items-center gap-8 animate-fade-in shadow-xl p-8">
    <div className="w-48 h-48 sm:w-60 sm:h-60 rounded-xl overflow-hidden flex-shrink-0 mb-6 md:mb-0">
      {member.imageUrl ? (
        <img
          src={member.imageUrl}
          alt={member.name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-cyan-100">
          <User className="w-20 h-20 text-cyan-600" />
        </div>
      )}
    </div>
    <div>
      <h3 className="text-2xl font-display font-bold mb-2">{member.name}</h3>
      <p className="text-accent font-semibold mb-2">{member.position}</p>
      {member.qualification && (
        <p className="text-muted-foreground mb-4">{member.qualification}</p>
      )}
      {member.bio && (
        <p className="text-muted-foreground">{member.bio}</p>
      )}
    </div>
  </div>
);

// --- Compact Card Component (For Academic Head / Support Staff) ---
const CompactCard: React.FC<{ member: TeamMember }> = ({ member }) => (
  <div className="bg-white rounded-xl shadow-lg text-foreground animate-slide-up shadow-xl p-6 md:p-8">
    <div className="flex flex-col sm:flex-row items-center gap-6">
      <div className="w-40 h-48 rounded-lg overflow-hidden flex-shrink-0">
        {member.imageUrl ? (
          <img
            src={member.imageUrl}
            alt={member.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-cyan-100">
            <User className="w-16 h-16 text-cyan-600" />
          </div>
        )}
      </div>
      <div className="text-start">
        <h3 className="text-xl font-display font-bold mb-1 text-center sm:text-left">{member.name}</h3>
        <p className="text-accent font-medium mb-2 text-center sm:text-left">{member.position}</p>
        {member.qualification && (
          <p className="text-sm text-muted-foreground mb-4 text-center sm:text-left">{member.qualification}</p>
        )}
        {member.bio && (
          <p className="text-muted-foreground text-sm">{member.bio}</p>
        )}
      </div>
    </div>
  </div>
);

const TuitionPointTeam: React.FC = () => {
  const { members, loading, error } = useTeam(false); // Only fetch active members

  // Filter members by role/position
  // Leadership: positions containing "Director", order < 10
  // Academic Head: position containing "Head", order 10-20
  // Teachers: order 20-100
  // Support Staff: order > 100
  const leadership = members.filter(m =>
    m.position.toLowerCase().includes('director') || m.order < 10
  );

  const academicHead = members.find(m =>
    m.position.toLowerCase().includes('head') && m.order >= 10 && m.order < 20
  );

  const teachers = members.filter(m =>
    m.order >= 20 && m.order < 100 &&
    !m.position.toLowerCase().includes('director') &&
    !m.position.toLowerCase().includes('head')
  );

  const supportStaff = members.filter(m => m.order >= 100);

  if (loading) {
    return (
      <div className="bg-accent text-white min-h-screen">
        <SEO title="Our Team | Tuition Point Qatar" />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-12 h-12 animate-spin text-white" />
          <span className="ml-4 text-xl">Loading team members...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-accent text-white min-h-screen">
        <SEO title="Our Team | Tuition Point Qatar" />
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <p className="text-xl mb-4">Unable to load team members</p>
            <p className="text-white/70">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-accent text-white min-h-screen">
      <SEO
        title="Experienced Teachers & Tutors in Al Wakra | Tuition Point Qatar"
        description="Meet our qualified teaching team at Tuition Point Al Wakra. 15+ years experience, IIT-JEE, NEET, CBSE, IGCSE, IB specialists. M.Sc., B.Ed. certified educators dedicated to student success in Qatar."
        keywords="qualified teachers Qatar, experienced tutors Al Wakra, IIT-JEE coaching Qatar, NEET preparation Qatar, M.Sc teachers, B.Ed certified tutors, chemistry teacher Qatar, math teacher Al Wakra, physics tutor Qatar, meet our team"
      />

      {/* --------------------- Page Header --------------------- */}
      <section className="pt-16 pb-12 md:pt-20 md:pb-16 bg-accent">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 animate-slide-up">
              Experienced Teachers & Tutors in Al Wakra, Qatar
            </h1>
            <p className="text-lg text-white/90 animate-slide-up">
              Meet our qualified educators: 15+ years experience, M.Sc., B.Ed. certified, specializing in CBSE, IGCSE, IB, IIT-JEE, and NEET coaching.
            </p>
          </div>
        </div>
      </section>

      {/* --------------------- Leadership Section --------------------- */}
      {leadership.length > 0 && (
        <section className="py-24 bg-accent">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">
                Leadership Team - Qatar
              </h2>
              <p className="text-lg text-white/80 animate-slide-up">
                Experienced education leaders guiding Tuition Point's commitment to K-12 excellence in Al Wakra.
              </p>
            </div>

            <div className="max-w-4xl mx-auto space-y-8">
              {leadership.map((member) => (
                <LeadershipCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --------------------- Teachers Section --------------------- */}
      {(academicHead || teachers.length > 0) && (
        <section className="py-24 bg-accent">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">
                Qualified Teachers in Al Wakra
              </h2>
              <p className="text-lg text-white/80 animate-slide-up">
                M.Sc., B.Ed., and M.Tech certified educators specializing in CBSE, IGCSE, IB, and entrance exam coaching.
              </p>
            </div>

            {/* Academic Head - Special Horizontal Layout */}
            {academicHead && (
              <div className="max-w-2xl mx-auto mb-16">
                <CompactCard member={academicHead} />
              </div>
            )}

            {/* All other Teachers - Grid Layout */}
            {teachers.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {teachers.map((member, index) => (
                  <TeacherCard
                    key={member.id}
                    member={member}
                    delay={(index % 3) + 1}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* --------------------- Support Staff Section --------------------- */}
      {supportStaff.length > 0 && (
        <section className="py-24 bg-accent">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-4xl font-display font-semibold text-white mb-4 animate-slide-up">
                Support Staff
              </h2>
              <p className="text-lg text-white/80 animate-slide-up">
                The team that ensures smooth operations and excellent service.
              </p>
            </div>

            <div className="max-w-2xl mx-auto space-y-8">
              {supportStaff.map((member) => (
                <CompactCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* --------------------- Join Our Team CTA --------------------- */}
      <section className="py-24 bg-accent text-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-6 animate-slide-up">
              Join Our Teaching Team in Al Wakra
            </h2>
            <p className="text-lg text-white text-opacity-90 mb-8 animate-slide-up">
              We're seeking qualified educators with M.Sc., B.Ed., or equivalent qualifications to teach CBSE, IGCSE, and IB students in Qatar. If you're dedicated to making a difference in students' lives, we'd love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-slide-up">
              <Link
                to="/contact"
                className="border-2 border-white text-white hover:bg-white hover:text-accent font-semibold py-3 px-8 rounded-lg transition duration-300 sm:w-auto text-lg"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TuitionPointTeam;
